import { useTranslation } from "react-i18next";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { Variables } from "graphql-request";
import { useAlerts } from "context/AlertContext";
import { services } from "services";
import { AlertSeverity } from "utils/constants/alerts.constants";
import handleRetryFailure from "utils/helpers/handleRetryFailure";
import { TReactQueryObserverOptions } from "utils/types";

interface IGraphQLQueryParams<TQueryFnData, TVariables, TSelectedData> {
  query: string;
  variables?: TVariables;
  queryKey: string[];
  errorMessageKey?: string;
  queryOptions?: TReactQueryObserverOptions<TQueryFnData, Error, TSelectedData>;
}

const useGraphQLQuery = <
  TQueryFnData,
  TVariables extends Variables = {},
  TSelectedData = TQueryFnData,
>({
  query,
  variables,
  queryKey,
  errorMessageKey,
  queryOptions = {},
}: IGraphQLQueryParams<
  TQueryFnData,
  TVariables,
  TSelectedData
>): UseQueryResult<TSelectedData, Error> => {
  const { t } = useTranslation("common", {
    keyPrefix: "notifications",
  });

  const { showAlert } = useAlerts();

  return useQuery<TQueryFnData, Error, TSelectedData>({
    queryKey: [...queryKey, ...Object.values(variables ?? {})],
    queryFn: () =>
      services.graphQL.request<TQueryFnData, TVariables>(query, variables),
    retry: handleRetryFailure(() =>
      showAlert({
        severity: AlertSeverity.Error,
        message: t(errorMessageKey ?? "somethingWentWrong"),
      }),
    ),

    ...queryOptions,
  });
};

export default useGraphQLQuery;
